<template>
  <div :class="$style.container">
    <ActivityLogViewsDropdown
      :class="$style.dropdown"
      :currentValueEdited="(selectedView && !selectedView.isQuickView) && viewCanBeSaved"
      :value="value"
      @input="(value) => $emit('input', value)"
    />
    <div :class="$style.saveButtonContainer">
      <FlatActionButton
        :action="handleSaveClick"
        :class="$style.button"
        :disabled="(selectedView && selectedView.isQuickView) || !viewCanBeSaved"
      >
        save view
      </FlatActionButton>
    </div>
    <FlatActionButton
      :action="handleSaveAsClick"
      :class="$style.button"
      :disabled="value === null"
    >
      save as...
    </FlatActionButton>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { get, isEqual } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { notify } from 'client/util';
import { quickPicksMetadata, quickPicksOptions } from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';

import ActivityLogCreateViewModal from './ActivityLogCreateViewModal.vue';
import ActivityLogViewsDropdown from './ActivityLogViewsDropdown.vue';

const { ItemTypeConsts, StoreModuleMap } = Constants;
const { error, success } = Constants.default;
const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];

export default {
  name: 'ActivityLogViews',
  components: {
    ActivityLogViewsDropdown,
    FlatActionButton,
  },

  props: {
    columnNames: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    ...mapState(insightsEventModule, [
      'filters',
      'quickPickDate',
      'selectedView',
    ]),

    currentViewData() {
      // Database wants `values` instead of `value`
      const filters = this.filters.map(filter => ({ ...filter, values: filter.value }));

      const quickPick = this.quickPickDate || quickPicksOptions.oneHour;
      return {
        columns: this.columnNames,
        filters,
        range: quickPicksMetadata[quickPick].referenceValue,
      };
    },

    viewCanBeSaved() {
      if (!this.selectedView) return true;
      if (!this.quickPickDate) return true;
      return !isEqual(this.selectedView.columns, this.columnNames)
        || !isEqual(this.selectedView.filters, this.filters)
        || this.selectedView.range !== quickPicksMetadata[this.quickPickDate].referenceValue;
    },
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'createSavedView',
      'modifySavedView',
      'setSelectedView',
    ]),

    createView(name) {
      this.createSavedView({
        ...this.currentViewData,
        name,
      })
        .then((newView) => {
          notify(success, 'Saved View created!');
          this.$emit('input', newView);
        })
        .catch((err) => {
          notify(error, get(err, 'response.data.message') || 'Error creating Saved View');
        })
        .finally(ModalEventBus.close);
    },

    handleSaveAsClick() {
      return this.openCreateViewModal();
    },

    handleSaveClick() {
      if (this.selectedView && !this.selectedView.isQuickView) {
        // User is currently in a Saved View, so we should save over the current one
        return this.openModifyViewModal();
      }
      return this.openCreateViewModal();
    },

    modifyView() {
      return this.modifySavedView({
        id: this.selectedView.id,
        view: {
          ...this.currentViewData,
          name: this.selectedView.name,
        },
      })
        .then((view) => {
          this.setSelectedView(view);
          notify(success, 'Saved View saved!');
        })
        .catch((err) => {
          notify(error, get(err, 'response.data.message') || 'Error saving Saved View');
        })
        .finally(ModalEventBus.close);
    },

    openCreateViewModal() {
      ModalEventBus.open({
        component: ActivityLogCreateViewModal,
        componentProps: {
          handleContinue: this.createView,
        },
      });
    },

    openModifyViewModal() {
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          mdTemplate: `Are you sure you want to save over this Saved View, **${this.selectedView.name}**?`,
          continueText: 'save',
          handleContinue: () => {
            this.modifyView();
          },
          headerText: 'Modify Saved View',
        },
      });
    },
  },
};
</script>

<style module>
.container {
  align-items: flex-end;
  display: flex;
  position: relative;
}

.dropdown { display: inline-block; }

.button {
  margin-left: 8px !important; /* override .jc-ux2 button legacy styles */
  white-space: nowrap;
}

.saveButtonContainer {
  position: relative;
}
</style>
